// Video-consult PiP: short-lived "already joined" markers that keep a
// just-joined consult from re-ringing during the local-join -> Daily-webhook
// persistence window (a few seconds). Once the backend persists the join
// webhook, its discovery predicate excludes the consult on its own and the
// marker becomes a harmless no-op until it expires.
//
// Storage is localStorage, ONE key PER consult: auth is shared across tabs,
// so a per-tab (sessionStorage) marker would let a sibling tab ring for the
// consult this tab just joined. Markers hold consult ids + timestamps only
// (no PII); TTL expiry and logout clearAll() bound their lifetime. There is
// deliberately NO reconciliation against poll responses: a cross-tab poll
// that was issued before the consult existed could otherwise delete a fresh
// marker (see VIDEO-CONSULT-PIP-IMPLEMENTATION-PLAN.md, Step 1). Polls only
// ever READ suppression state.
//
// Every export is non-throwing by contract: storage access is wrapped, stored
// values are schema-validated (malformed entries treated as absent), and an
// in-memory map takes over when localStorage is unavailable: a storage
// failure must degrade to weaker (same-tab-only) suppression, never break a
// caller.
//
// The read path is strictly PURE: isSuppressed never deletes expired or
// malformed markers, because a read-then-delete from one tab can race a
// concurrent fresh markJoined for the same consult in another tab and delete
// the fresh marker (restoring the quick-hangup re-ring window). Physical
// cleanup is logout clearAll only; an expired marker lingering as a few bytes
// is harmless since expiry already makes it a no-op.

const KEY_PREFIX = "videoConsult:suppressed:";

// Generously covers webhook persistence latency. If the join webhook was LOST,
// expiry deliberately lets the alert return: backend truth ("never joined")
// wins, and the re-surfaced alert is the recovery affordance.
export const SUPPRESSION_TTL_MS = 3 * 60 * 1000;

interface SuppressionMarker {
  markedAt: number;
  expiresAt: number;
}

// Fallback when localStorage throws (disabled storage, quota). Same-tab
// suppression keeps working; only sibling-tab coverage is lost.
const memoryStore = new Map<string, string>();

const keyFor = (consultId: number): string => `${KEY_PREFIX}${consultId}`;

const readRaw = (key: string): string | null => {
  try {
    const stored = window.localStorage.getItem(key);
    if (stored !== null) {
      return stored;
    }
  } catch {
    // fall through to the in-memory fallback
  }
  return memoryStore.get(key) ?? null;
};

const writeRaw = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
    return;
  } catch {
    memoryStore.set(key, value);
  }
};

const parseMarker = (raw: string): SuppressionMarker | null => {
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      typeof parsed.markedAt === "number" &&
      typeof parsed.expiresAt === "number"
    ) {
      return parsed as SuppressionMarker;
    }
  } catch {
    // malformed JSON, treated as no marker
  }
  return null;
};

/**
 * Record that the pharmacist actually joined the Daily room for this consult.
 * Call ONLY on Daily's `joined-meeting` event, never during prejoin, so a
 * prejoin cancel is never suppressed.
 */
export const markJoined = (consultId: number): void => {
  try {
    const now = Date.now();
    const marker: SuppressionMarker = {
      markedAt: now,
      expiresAt: now + SUPPRESSION_TTL_MS,
    };
    writeRaw(keyFor(consultId), JSON.stringify(marker));
  } catch {
    // non-throwing by contract
  }
};

/**
 * Expiry-aware read of a single consult's marker. Pure read: expired or
 * malformed markers are ignored, never deleted (see the header note on the
 * cross-tab read-then-delete race).
 */
export const isSuppressed = (consultId: number): boolean => {
  try {
    const raw = readRaw(keyFor(consultId));
    if (raw === null) {
      return false;
    }
    const marker = parseMarker(raw);
    return marker !== null && Date.now() < marker.expiresAt;
  } catch {
    return false;
  }
};

/**
 * Reverse of keyFor: the consult id when the given storage key is a
 * suppression marker key, null otherwise. Lets the cross-tab `storage`
 * event listener (VideoConsultManager) recognize marker writes from sibling
 * tabs without duplicating the key format.
 */
export const parseSuppressionKey = (key: string | null): number | null => {
  if (!key || !key.startsWith(KEY_PREFIX)) {
    return null;
  }
  // Digits only: rejects the bare prefix (Number("") is 0), fractions,
  // negatives, and exponent notation: consult ids are positive integers.
  const suffix = key.slice(KEY_PREFIX.length);
  if (!/^\d+$/.test(suffix)) {
    return null;
  }
  return Number(suffix);
};

/**
 * Remove every suppression marker (namespace-prefixed keys only). Hooked into
 * the central `auth/logout` reducer so markers never cross an auth boundary.
 */
export const clearAll = (): void => {
  memoryStore.clear();
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key !== null && key.startsWith(KEY_PREFIX)) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // storage unavailable: the memory copy is already cleared
  }
};
